/**
 * What a block renderer is given.
 *
 * Kept deliberately small. Blocks emit their own telemetry by importing `track` directly — the
 * whole point of the maintained renderer is that instrumentation is not something a caller can
 * forget to wire — so the context only carries the things a block genuinely cannot derive.
 */

import type { AnswerPayload } from "../../shared/api.js";

export interface RenderContext {
  /** Send text to the tutor as a chat message, immediately. Used by one-tap pills. */
  /** False when it was not sent — the tutor is still busy with something else. */
  ask(text: string): boolean;
  /** Build a row of one-tap suggestion pills. */
  buildPills(texts: readonly string[], wide?: boolean): HTMLUListElement;
  /** Absolute origin serving sandboxed lesson artifacts. Never the app's own origin. */
  lessonOrigin: string;
  /** Resolved once at render time so every sim and transition agrees. */
  reducedMotion: boolean;
  /**
   * Reported by choice/order/match after each graded response, so the renderer can notice when
   * the lesson has produced all the evidence it is going to.
   */
  answered(blockId: string, payload: AnswerPayload): void;
}

/** Wraps a block's content in the standard shell: a section that telemetry can attribute to. */
export function blockShell(type: string, id: string | undefined): HTMLElement {
  const section = document.createElement("section");
  section.className = `block block-${type}`;
  if (id !== undefined) section.dataset["blockId"] = id;
  return section;
}

/** A live region for feedback. Announced without stealing focus. */
export function liveRegion(className = "feedback"): HTMLElement {
  const node = document.createElement("div");
  node.className = className;
  node.setAttribute("role", "status");
  node.setAttribute("aria-live", "polite");
  return node;
}
